import React, { ReactNode, useEffect, useState } from "react";
import { Box, Grid, MenuItem, Paper, Select } from "@mui/material";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  IntegrationType,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { Controller, useForm } from "react-hook-form";
import { Space } from "~/lib/types";
import { TicketIntegration } from "~/pages/tickets/components/TicketSettings/types";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { AzureDevOpsFormattedDestination } from "~/pages/integrations/hosted-integrations/forms/azure-devops/components/AzureDevOpsFormattedDestination";
import { AzureDevOpsFormInput } from "~/pages/integrations/hosted-integrations/forms/azure-devops/AzureDevOpsIntegrationForm";
import { useAzureTicketContext } from "~/hooks/useAzureTicketContext";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CaseSettingsIntegrationProps = {
  integration: TicketIntegration;
  space: Space;
  showAutoCreateSection: boolean;
};

export const TicketSettingsAzureIntegration = ({
  integration,
  space,
  showAutoCreateSection,
}: CaseSettingsIntegrationProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm<AzureDevOpsFormInput>(
    {
      defaultValues: {
        defaultProject: "",
        autoCloseTickets: false,
        autoCreateTickets: false,
      },
      mode: "onChange",
    },
  );
  const { projects, isProjectsEmpty } = useAzureTicketContext({
    scopeMrn: integration?.mrn || "",
    skip: !integration?.mrn,
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const onSubmit = async (data: AzureDevOpsFormInput) => {
    const azureDevopsConfigurationOptions = {
      defaultProjectName: data.defaultProject || "",
      organizationUrl: data.organizationUrl,
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
      tenantId: data.tenantId,
      servicePrincipalId: data.servicePrincipalId,
      clientSecret: "",
    };

    await updateIntegration({
      variables: {
        input: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
          type: ClientIntegrationType.TicketSystemAzureDevops,
          configurationOptions: {
            azureDevopsConfigurationOptions,
          },
        },
      },
    });
  };

  useEffect(() => {
    // TypeScript users
    const subscription = watch(() => {
      isInitialized && handleSubmit(onSubmit)();
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, isInitialized]);

  const [watchAutoCreate] = watch(["autoCreateTickets"]);

  useEffect(() => {
    const configOptions = integration?.configurationOptions;
    if (configOptions?.__typename !== "AzureDevopsConfigurationOptions") return;
    reset({
      defaultProject: configOptions.defaultProjectName || "",
      organizationUrl: configOptions.organizationUrl,
      integrationName: integration?.name || "",
      autoCloseTickets: configOptions.autoCloseTickets,
      autoCreateTickets: configOptions.autoCreateTickets,
      tenantId: configOptions.tenantId,
      servicePrincipalId: configOptions.servicePrincipalId,
    });
    setIsInitialized(true);
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <JiraAutoConfigurationSection
          control={control}
          ticketType={IntegrationType.TicketSystemAzureDevops}
          showAutoCreateSection={showAutoCreateSection}
          autoCreateSubField={
            <Box
              component={motion.div}
              initial={false}
              transition={{
                duration: 0.35,
                staggerChildren: 0.25,
              }}
              sx={{ overflow: "hidden" }}
            >
              <Controller
                name="defaultProject"
                {...{ control }}
                render={({ field }) => (
                  <SubField
                    configuration="Select a drift destination"
                    caption="Select where to create an issue for each new drift ticket."
                    disabled={!watchAutoCreate}
                    field={
                      <Grid
                        sx={{ alignSelf: "flex-end" }}
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <Select
                          {...field}
                          fullWidth
                          disabled={!watchAutoCreate}
                          sx={{
                            "&.MuiInputBase-root": {
                              bgcolor: "code.background",
                            },
                          }}
                          displayEmpty
                          renderValue={(selected: ReactNode) => {
                            const selectedProject = projects?.find(
                              (project) => project.name === selected,
                            );

                            const selectedProjectTitle = (
                              <AzureDevOpsFormattedDestination
                                projectTitle={selectedProject?.name || ""}
                              />
                            );

                            if (isProjectsEmpty) {
                              return (
                                <>
                                  No projects available – create one in Azure
                                  DevOps first
                                </>
                              );
                            }

                            if (!selected || !selectedProjectTitle) {
                              return <>Select project</>;
                            }

                            return <>{selectedProjectTitle}</>;
                          }}
                        >
                          {(projects || []).map((project) => (
                            <MenuItem key={project.name} value={project.name}>
                              {project.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    }
                  />
                )}
              />
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};
